
import React, { useEffect } from 'react';
import { AccordionSummary, AccordionDetails, Typography, Box } from '@mui/material';
import CustomAccordion from '../Accordian/accordian';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { facility } from '../../models/DashboardInformation';
import { Deal } from '../../models/Deal'; // Adjust the import path as necessary
import { Button } from '@mui/material';
import { DataGrid, GridColDef, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarDensitySelector } from '@mui/x-data-grid';
import { useNavigate, useParams } from 'react-router-dom';
import { useFetchDealsByFacilityQuery, useGetAvailableUnitsDataQuery, useGetOperatorGuidesQuery } from '../../api/endpoints/apiConfig';
import { toast } from 'react-toastify';
import CircularProgress from "@mui/material/CircularProgress";
interface FacilityInformationProps {
    data: facility | null;
}

function CustomToolbar() {
    return (
        <GridToolbarContainer>
            <GridToolbarColumnsButton />
            <GridToolbarDensitySelector
                slotProps={{ tooltip: { title: 'Change density' } }}
            />
        </GridToolbarContainer>
    );
}
const ActiveReservation: React.FC<FacilityInformationProps> = ({ data }) => {
    // Call hooks unconditionally
    const navigate = useNavigate();
    const params = useParams();
    const facilityId = params?.facilityid;
    // Ensure that even if `facilityId` is invalid, the hook is still called with a valid argument (0).
    const { data: responseData, isLoading,isFetching, isError, refetch } = useFetchDealsByFacilityQuery(facilityId ? parseInt(facilityId) : 0);

    // Second API call for available units
    const { data: availableUnitsData } = useGetAvailableUnitsDataQuery(facilityId ? parseInt(facilityId) : 0);
    const SettingData = sessionStorage.getItem('setting');
    const Settings = SettingData ? JSON.parse(SettingData) : [];
    const IsRent = Settings.can_rent;
    const operatorId = sessionStorage.getItem('operatorId');
    const { data: FacilityOperatorAlerts } = useGetOperatorGuidesQuery(operatorId ? parseInt(operatorId) : 0);
    useEffect(() => {
        if (facilityId) {
            refetch();
        }
    }, [facilityId, refetch]); // Add refetch to the dependency array

    // Save available units data to session storage
    useEffect(() => {
        if (availableUnitsData) {
            sessionStorage.setItem('availableUnitsData', JSON.stringify(availableUnitsData));
        }
    }, [availableUnitsData]);

    const promotionsMap: Record<string, any> = {};
    const promos = data?.promotions ?? [];
    for (const p of promos) {
        const key = String(p?.external_id ?? '').trim();
        if (key) promotionsMap[key] = p;
    }

    const handleManageClick = (params: any) => {
        const data = params.row;
        navigate(`/ManageReservation/${data.facility_id}/${data.external_id}`, { state: { data } });
    };


    // Map deals data safely
    const deals: Deal[] = responseData?.deals || [];
    const formatPhoneNumber = (phone: string): string => {
        if (phone.length < 10) return 'Invalid Number'; // Check for valid phone number length
        return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    };

    // Step 3: Map through deals and assign promoText
    const rowData = Array.isArray(deals)
        ? deals.map((deal) => {
            // Find the corresponding unit data using external_unit_id
            // Check if deal.external_promotion_id is defined before using it as an index
            const promoId =
                deal.external_promotion_id != null && String(deal.external_promotion_id).trim() !== ''
                    ? String(deal.external_promotion_id).trim()
                    : '';
            const promotion = promoId ? promotionsMap[promoId] : null;
            const promoText =
                promotion?.name || (promoId ? `Promotion #${promoId}` : '');
            // Return the modified row data
            return {
                id: deal.id,
                firstName: deal?.first_name,
                lastName: deal?.last_name,
                phone: deal?.phone,
                email: deal?.email,
                type: deal?.type,
                quotedRate: `${deal?.quoted_price.toFixed(2)}`,
                promo: promoText, // Safely assigned promotion text
                moveInDate: deal?.move_in_date,
                external_promotion_id: deal?.external_promotion_id,
                unit_dimensions: deal?.unit_dimensions,
                external_unit_id: deal?.external_unit_id,
                external_id: deal?.external_id,
                facility_id: deal?.facility_id,
                rent: deal?.quoted_price,
            };
        })
        : [];
    const handleRentReservation = (deal: any) => {
        const { row } = deal;
        if (!row) {
            toast.error('Row data is missing from the deal object.');
            return;
        }
        // Check if the promotion exists in the map


        const rentalData = {
            promotion: row.promo,
            facilityId: row.facility_id,
            externalUnitId: row.external_unit_id,
            external_promotion_id: row.external_promotion_id,
            id: row.id,
            externalId: row.external_id,
        };
        // Navigate to the Rent page with both IDs and state
        navigate(`/rent/${rentalData.facilityId}/deal/${rentalData.externalId}`, { state: { rentalData } });
    };
    // Column Definitions
    const colDefs: GridColDef[] = [
        { field: "firstName", headerName: "First Name", flex: 1, minWidth: 53, headerAlign: 'center', align: 'center', hideable: false },
        { field: "lastName", headerName: "Last Name", flex: 1, minWidth: 53, headerAlign: 'center', align: 'center', hideable: false },
        {
            field: "phone",
            headerName: "Phone",
            minWidth: 50,
            headerAlign: 'center', align: 'center',
            flex: 1,
            valueGetter: (params) => {
                const phone = params; // Accessing the phone directly

                return phone ? formatPhoneNumber(phone) : 'N/A'; // Assuming formatPhoneNumber is a valid function
            },
        },
        { field: "email", headerName: "Email", flex: 1, minWidth: 88, headerAlign: 'center', align: 'center', sortable: false },
        { field: "type", headerName: "Type", flex: 1, minWidth: 200, headerAlign: 'center', align: 'center', sortable: false },
        {
            field: "quotedRate", headerName: "Quoted Rate", flex: 1, minWidth: 60, disableReorder: true, headerAlign: 'center', align: 'center', sortable: false,
            valueGetter: (params) => {
                const quotedPrice = parseFloat(params); // Ensure the value is a number
                return `$${quotedPrice.toFixed(2)}`; // Format the number with a dollar sign
            },
        },
        { field: "promo", headerName: "Promotion", flex: 1, minWidth: 88, sortable: false, },
        {
            field: "moveInDate", headerName: "Move-In Date", flex: 1, minWidth: 60,
            valueFormatter: (value: string | Date) => {
                // Create a date object and check if it's a string in 'YYYY-MM-DD' format
                let date: Date;

                if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
                    // Append 'T00:00:00Z' to treat it as a UTC date
                    date = new Date(value + 'T00:00:00Z');
                } else {
                    // Use the value directly
                    date = new Date(value);
                }

                // Check if the date is valid
                if (isNaN(date.getTime())) {
                    return 'Invalid Date'; // Provide fallback for invalid dates
                }

                // Format the date in 'en-US' format using UTC
                return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    timeZone: 'UTC' // Ensure it's displayed in UTC
                });
            }
        },
        {
            field: 'action',
            headerName: 'Action',
            flex: 1,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <div style={{ display: 'flex', gap: '18px' }}>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => {
                                handleManageClick(params);
                            }}
                        >
                            Manage
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            disabled={!IsRent} // Disable the button if rentEnabled is 0
                            onClick={() => handleRentReservation(params)}
                        >
                            Rent
                        </Button>
                        {/* <Button size="small" variant="contained">Rent</Button> */}
                    </div>
                </div>
            ),
        },
    ];
    // Now handle the case where `data` is null
    if (!data) {
        return (
            <CustomAccordion defaultExpanded>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                >
                    <Typography variant="h3" color="text.secondary">Active Reservation</Typography>
                </AccordionSummary>
                <AccordionDetails>No Data Found</AccordionDetails>
            </CustomAccordion>
        );
    }
    const removeHtmlTags = (text: string | null | undefined): string => {
        if (!text) {
            return ''; // Return an empty string for null or undefined input
        }
        // Decode HTML entities and normalize spaces
        const textArea = document.createElement('textarea');
        textArea.innerHTML = text;
        const decodedText = textArea.value.replace(/\s+/g, ' ').trim();

        return decodedText;
    };
    const OperatorAlert = FacilityOperatorAlerts?.guides || [];
    console.log('Operator Alert', OperatorAlert);
    const htmlContent = removeHtmlTags(
        data.guides?.[0]?.reservation_management_notes || OperatorAlert[0]?.reservation_management_notes || ''
    );
    console.log('HtmlContent', htmlContent)
    // Return the content for when there is data
    return (
        <CustomAccordion defaultExpanded>
             <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header">
                <Box display="flex" alignItems="center" width="100%">
                    <Typography variant="h3" color="text.secondary" flexGrow={1}>
                    Active Reservation ({deals.length})
                    </Typography>
                    {(isLoading || isFetching) && (
                        <CircularProgress size={20} sx={{ ml: 2 }} />
                    )}
                </Box>
            </AccordionSummary>
            {/* <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header" >
                <Typography variant="h3" color="text.secondary">Active Reservation ({deals.length})</Typography>
            </AccordionSummary> */}
            <AccordionDetails>
                {(data.guides?.[0]?.reservation_management_notes || OperatorAlert[0]?.reservation_management_notes) && (
                    <Box my={2} p={2.5} bgcolor={'#EBF3FF'}>
                        <Typography
                            color="text.primary"
                            textAlign="left"
                            dangerouslySetInnerHTML={{ __html: htmlContent }}
                        />
                    </Box>
                )}

                <Box sx={{ width: '100%', overflow: 'auto' }}>
                    {isLoading ? (
                        <Typography>Loading...</Typography>
                    ) : isError ? (
                        <Typography>Error loading deals</Typography>
                    ) : (
                        <DataGrid
                            rows={rowData}
                            columns={colDefs}

                            initialState={{

                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns some columns.  the other columns will remain visible
                                        phone: true,
                                        email: true,
                                        type: false,
                                    },
                                },
                                sorting: {
                                    sortModel: [{ field: 'moveInDate', sort: 'asc' }],
                                },

                            }}

                            autoHeight // Automatically adjust the height of the DataGrid to fit all rows
                            slots={{
                                toolbar: CustomToolbar,
                            }}
                            disableColumnMenu

                        />
                    )}
                </Box>

            </AccordionDetails>
        </CustomAccordion>
    );
};

export default ActiveReservation;
